import { useLayoutEffect, useRef, useState } from 'react';
import { AppWithFavorite } from '@/types';
import { Card } from './Card';
import { Star, ExternalLink, Info } from 'lucide-react';
import { cn } from '@/utils/cn';

interface AppCardProps {
  app: AppWithFavorite;
  onToggleFavorite: (appId: string) => void;
  onAccess: (appId: string, url: string) => void;
}

export function AppCard({ app, onToggleFavorite, onAccess }: AppCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [isClipped, setIsClipped] = useState(false);
  const descRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = descRef.current;
    if (!el || !app.description || expanded) return;
    setIsClipped(el.scrollHeight > el.clientHeight + 1);
  }, [app.description, expanded]);

  const handleClick = () => {
    if (app.comingSoon) return;
    onAccess(app.id, app.url);
    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite(app.id);
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(v => !v);
  };

  return (
    <Card
      className={cn(
        'group relative overflow-hidden transition-all duration-200',
        app.comingSoon
          ? 'cursor-default opacity-75'
          : 'cursor-pointer hover:shadow-lg hover:scale-[1.02]'
      )}
      onClick={handleClick}
      aria-disabled={app.comingSoon || undefined}
    >
      {!app.comingSoon && (
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      )}

      <div className="relative p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {app.icon ? (
              <div className="text-4xl">{app.icon}</div>
            ) : (
              <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold text-xl">
                {app.name.charAt(0)}
              </div>
            )}
          </div>

          <button
            onClick={handleFavoriteClick}
            className={cn(
              'p-2 rounded-lg transition-colors',
              app.isFavorite
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            )}
            aria-label={app.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star
              className={cn('w-5 h-5', app.isFavorite && 'fill-current')}
            />
          </button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{app.name}</h3>
            {!app.comingSoon && (
              <ExternalLink className="w-4 h-4 text-gray-600 dark:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </div>

          {app.description && (
            <div className="flex items-start gap-1.5">
              <p
                ref={descRef}
                className={cn(
                  'text-sm text-gray-600 dark:text-gray-400 min-w-0 flex-1',
                  !expanded && 'line-clamp-2'
                )}
              >
                {app.description}
              </p>
              {(isClipped || expanded) && (
                <button
                  type="button"
                  onClick={handleInfoClick}
                  className="shrink-0 mt-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  aria-label={expanded ? 'Show less' : 'Show full description'}
                  aria-expanded={expanded}
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}

          {app.comingSoon ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
              Coming soon
            </span>
          ) : (
            app.category && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                {app.category}
              </span>
            )
          )}
        </div>
      </div>
    </Card>
  );
}
